import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import type { CrmDataProvider } from "../providers/types";
import type { MailCheck, MailScope } from "../providers/vv/dataProvider";
import type { Sale } from "../types";
import {
  EMPTY_MAILBOX as EMPTY,
  MAIL_SETTINGS_KEY,
  mailboxFields,
  type MailboxFormValues as Form,
} from "./mailboxFields";

const PRESETS = [
  { name: "Яндекс", smtp: "smtp.yandex.ru", imap: "imap.yandex.ru" },
  { name: "Mail.ru", smtp: "smtp.mail.ru", imap: "imap.mail.ru" },
  { name: "Gmail", smtp: "smtp.gmail.com", imap: "imap.gmail.com" },
];

const errorMessage = (error: unknown) =>
  (error as { body?: { message?: string } })?.body?.message ??
  (error as Error)?.message ??
  String(error);

const smtpReady = (keys: string[]) =>
  ["smtp_host", "smtp_user", "smtp_password"].every((k) => keys.includes(k));

/**
 * A mailbox: the user's own (profile «Почта») or the company's (Подключения).
 * Values are write-only: the page only knows which fields are filled.
 * «Проверить» has the server log in to SMTP and IMAP with the saved fields.
 */
export const MailboxForm = ({ scope }: { scope: MailScope }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: MAIL_SETTINGS_KEY,
    queryFn: () => dataProvider.getMailSettings(),
  });
  const keys = settings?.[scope]?.keys ?? [];
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState<MailCheck | null>(null);
  const id = useId();

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const fail = (error: unknown) =>
    notify(errorMessage(error), {
      type: "error",
      messageArgs: { _: errorMessage(error) },
    });
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: MAIL_SETTINGS_KEY });

  const save = async () => {
    const fields = mailboxFields(form, keys);
    if (!Object.keys(fields).length) {
      notify("crm.mail.mailbox.nothing_typed", { type: "warning" });
      return;
    }
    setBusy(true);
    try {
      await dataProvider.saveMailbox(scope, fields);
      setForm(EMPTY);
      setCheck(null);
      notify("crm.mail.mailbox.saved", { type: "success" });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  const test = async () => {
    setBusy(true);
    try {
      setCheck(await dataProvider.testMailbox(scope));
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await dataProvider.clearMailbox(scope);
      setCheck(null);
      notify("crm.mail.mailbox.removed", { type: "success" });
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
      await refresh();
    }
  };

  const status = smtpReady(keys)
    ? translate("crm.mail.mailbox.status_ok")
    : scope === "me" && smtpReady(settings?.company?.keys ?? [])
      ? translate("crm.mail.mailbox.uses_company")
      : translate("crm.mail.mailbox.status_none");

  const field = (
    k: keyof Form,
    label: string,
    opts: {
      type?: string;
      placeholder?: string;
      hint?: string;
      isSet?: boolean;
      inputMode?: "numeric" | "email";
    } = {},
  ) => (
    <div className="flex flex-col gap-1 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={`${id}-${k}`} className="text-xs text-muted-foreground">
          {label}
        </label>
        {opts.isSet ? (
          <span className="text-xs" data-testid={`mail-set-${k}`}>
            {translate("crm.mail.mailbox.is_set")} ✓
          </span>
        ) : null}
      </div>
      <Input
        id={`${id}-${k}`}
        type={opts.type ?? "text"}
        inputMode={opts.inputMode}
        autoComplete="off"
        spellCheck={false}
        value={form[k]}
        onChange={set(k)}
        placeholder={opts.placeholder}
        className="h-9 min-w-0"
      />
      {opts.hint ? (
        <p className="text-xs text-muted-foreground">{opts.hint}</p>
      ) : null}
    </div>
  );

  return (
    <div className="flex flex-col gap-3 min-w-0" data-mailbox={scope}>
      <p className="text-sm" data-testid={`mailbox-status-${scope}`}>
        {status}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {translate("crm.mail.mailbox.presets")}:
        </span>
        {PRESETS.map((p) => (
          <Button
            key={p.name}
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() =>
              setForm((f) => ({
                ...f,
                smtp_host: p.smtp,
                smtp_port: "465",
                imap_host: p.imap,
                imap_port: "993",
              }))
            }
          >
            {p.name}
          </Button>
        ))}
      </div>
      <form
        className="flex flex-col gap-3 min-w-0"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {field("from_addr", translate("crm.mail.mailbox.from_addr"), {
            inputMode: "email",
            placeholder: "name@example.com",
            hint:
              scope === "me"
                ? translate("crm.mail.mailbox.from_hint")
                : undefined,
            isSet: keys.includes("from_addr"),
          })}
          {field("from_name", translate("crm.mail.mailbox.from_name"), {
            isSet: keys.includes("from_name"),
          })}
        </div>
        <div className="grid grid-cols-[1fr_5.5rem] gap-3">
          {field("smtp_host", translate("crm.mail.mailbox.smtp_host"), {
            placeholder: "smtp.yandex.ru",
            isSet: keys.includes("smtp_host"),
          })}
          {field("smtp_port", translate("crm.mail.mailbox.smtp_port"), {
            inputMode: "numeric",
            placeholder: "465",
          })}
          {field("imap_host", translate("crm.mail.mailbox.imap_host"), {
            placeholder: "imap.yandex.ru",
            isSet: keys.includes("imap_host"),
          })}
          {field("imap_port", translate("crm.mail.mailbox.imap_port"), {
            inputMode: "numeric",
            placeholder: "993",
          })}
        </div>
        {field("login", translate("crm.mail.mailbox.login"), {
          hint: translate("crm.mail.mailbox.login_hint"),
          isSet: keys.includes("smtp_user"),
        })}
        {field("password", translate("crm.mail.mailbox.password"), {
          type: "password",
          hint: translate("crm.mail.mailbox.password_hint"),
          isSet: keys.includes("smtp_password"),
        })}
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="outline" size="sm" disabled={busy}>
            {translate("crm.mail.mailbox.save")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || !keys.length}
            aria-label={translate("crm.mail.mailbox.check_label")}
            onClick={() => void test()}
          >
            {translate("crm.mail.mailbox.check")}
          </Button>
          {keys.length ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => void remove()}
            >
              {translate("crm.mail.mailbox.remove")}
            </Button>
          ) : null}
        </div>
      </form>
      {check ? (
        <ul className="flex flex-col gap-1 text-sm" data-testid="mail-check">
          {(["smtp", "imap"] as const).map((k) => (
            <li key={k} className="flex items-start gap-2">
              {check[k].ok ? (
                <Check className="size-4 shrink-0 mt-0.5" aria-label="ok" />
              ) : (
                <X
                  className="size-4 shrink-0 mt-0.5 text-destructive"
                  aria-label="error"
                />
              )}
              <span className="min-w-0 break-words">
                {k.toUpperCase()}: {check[k].message}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

/** Подключения: employees with a personal mailbox; the owner can remove one. */
export const PersonalMailboxes = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const { data: settings } = useQuery({
    queryKey: MAIL_SETTINGS_KEY,
    queryFn: () => dataProvider.getMailSettings(),
  });
  const people = settings?.people ?? [];
  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      filter: { id: people.map((p) => p.sales_id) },
      pagination: { page: 1, perPage: 200 },
    },
    { enabled: people.length > 0 },
  );
  const nameOf = (id: number) => {
    const s = sales?.find((x) => String(x.id) === String(id));
    return s ? `${s.first_name} ${s.last_name}` : `#${id}`;
  };
  const remove = async (id: number) => {
    try {
      await dataProvider.clearMailbox(id);
      notify("crm.mail.mailbox.removed", { type: "success" });
    } catch (error) {
      notify(errorMessage(error), {
        type: "error",
        messageArgs: { _: errorMessage(error) },
      });
    } finally {
      await queryClient.invalidateQueries({ queryKey: MAIL_SETTINGS_KEY });
    }
  };
  return (
    <div className="flex flex-col gap-2 min-w-0 border-t pt-4">
      <p className="text-sm font-medium">
        {translate("crm.connections.mail.people")}
      </p>
      {people.length ? (
        <ul className="flex flex-col divide-y">
          {people.map((p) => (
            <li
              key={p.sales_id}
              className="flex items-center justify-between gap-2 py-1.5 min-w-0"
            >
              <span className="text-sm truncate">{nameOf(p.sales_id)}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`${translate("crm.connections.mail.remove_person")}: ${nameOf(p.sales_id)}`}
                onClick={() => void remove(p.sales_id)}
              >
                {translate("crm.connections.remove")}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("crm.connections.mail.people_empty")}
        </p>
      )}
    </div>
  );
};
